import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, LogOut, Moon, Settings2, Sun } from "lucide-react";
import useDevTheme from "./useDevTheme";
import ContainerBooks from "./ContainerBooks";
import "./DeveloperDashboard.css";

/**
 * /dev/containers — CONTAINER MANAGEMENT, ON ITS OWN PAGE.
 *
 * It began life as a section at the top of /dev/dashboard and was moved here
 * because the two are different jobs. The dashboard answers "what is in the
 * stored history and how do I export it"; this answers "which books exist, and
 * what is each one's name, mode, start date and visibility". Sharing one page
 * meant the first thing you saw on the way to a download was a grid of forms.
 *
 * WHY IT DOES NOT TAKE A CONTAINER FROM THE URL. Every other developer page has
 * a container selector, because they SHOW one container's data. This page shows
 * ALL of them, always — it is the list, so there is nothing to select. Adding a
 * selector here would ask which container to look at while the whole point is
 * seeing every one side by side, disabled ones included.
 *
 * The section heading is the dashboard's own exported component, so the two
 * pages cannot drift apart in how a section is titled.
 */

export default function DeveloperContainers() {
  const navigate = useNavigate();
  /* `theme` is not decoration: every colour in DeveloperDashboard.css is
     namespaced under `.dev-root`, with the dark palette on
     `.dev-root.dev-theme-dark`. A wrapper carrying only `dev-root` renders the
     LIGHT palette whatever the toggle says — which is exactly what the browse
     page did until the class was put back. */
  const { theme, toggle, dark } = useDevTheme();
  const token = localStorage.getItem("token");

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    navigate("/");
  };

  return (
    <div className={`dev-root dev-theme-${theme}`} data-dev-theme={theme}>
      <div className="dev-glow" aria-hidden="true" />

      <header className="dev-topbar">
        <div className="dev-brand">
          <Link to="/dev/dashboard" className="dev-icon-btn dev-back" title="Back to the dashboard">
            <ArrowLeft size={16} aria-hidden="true" />
          </Link>
          <div className="dev-titles">
            <h1 className="dev-title">Containers</h1>
            <p className="dev-sub">every book, disabled ones included</p>
          </div>
        </div>

        <div className="dev-topbar-actions">
          <Link to="/dev/accounts" className="dev-ghost-btn" title="Brokers, users and accounts">
            <Settings2 size={15} aria-hidden="true" />
            <span>Accounts</span>
          </Link>
          <button
            type="button"
            className="dev-icon-btn"
            onClick={toggle}
            aria-label={dark ? "Switch to light theme" : "Switch to dark theme"}
            title={dark ? "Light theme" : "Dark theme"}
          >
            {dark ? <Sun size={16} aria-hidden="true" /> : <Moon size={16} aria-hidden="true" />}
          </button>
          <button type="button" className="dev-icon-btn" onClick={handleLogout} title="Sign out">
            <LogOut size={16} aria-hidden="true" />
          </button>
        </div>
      </header>

      <main className="dev-main">
        <ContainerBooks token={token} />
      </main>
    </div>
  );
}
